import { FC, useEffect, useRef, useState } from 'react';
import { saveAs } from 'file-saver';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileWord } from '@fortawesome/free-solid-svg-icons';
import { Button } from '@praktio/common-ui';

import './DocViewer.scss';
import { useDispatch, useSelector } from 'react-redux';
import { showOverlay } from '../../ducks/overlay';
import { RootState } from '../../ducks/store';
import classNames from 'classnames';

interface IProps {
  document?: IDocument;
  className?: string;
}

const DocViewer: FC<IProps> = ({ document, className }) => {
  const dispatch = useDispatch();
  const objectRef = useRef(null);
  const [url, setUrl] = useState<string | undefined>();
  const [objectLoaded, setObjectLoaded] = useState(false);

  const { overlayDisplayed } = useSelector((state: RootState) => {
    const show = state.overlay.show;
    return {
      overlayDisplayed: show,
    };
  });

  useEffect(() => {
    if (document && !(document as any).url) {
      setUrl(URL.createObjectURL(document as any));
    } else {
      setUrl((document as any)?.url);
    }
  }, [document]);

  let wordExtension = '';
  if (document?.wordUrl) {
    const index = document.wordUrl.lastIndexOf('.');
    wordExtension = document.wordUrl.slice(index);
  }

  return (
    <div className={classNames('DocViewer', 'mx-1', 'mx-sm-3', 'mx-md-5', 'mx-lg-7', className)}>
      {url && (
        <>
          {document?.wordUrl && objectLoaded && (
            <Button
              className="DocViewer-word-download-button d-flex align-items-center justify-content-center pointer"
              onClick={() => {
                if (document.wordUrl) {
                  dispatch(showOverlay(true));
                  saveAs(document.wordUrl, document?.wordName);
                  dispatch(showOverlay());
                }
              }}
              tooltip={`Download ${wordExtension || 'document'}`}
              tooltipPlacement="top"
              disabled={overlayDisplayed}
            >
              <FontAwesomeIcon icon={faFileWord} />
            </Button>
          )}
          <object
            onLoad={() => setObjectLoaded(true)}
            className="DocViewer-viewbox"
            data={`${url}`}
            title="Document"
            ref={objectRef}
          >
            <div className="pdf-error">
              <span className="warning-icon material-icons">warning</span>
              <div className="error-header">There was an issue loading this PDF.</div>
              <div className="error-text">
                This might be due to your browser settings. Confirm your browser settings allow
                opening PDFs in the browser, then refresh this page.
              </div>
              <div className="download-buttons">
                <Button
                  className="download-pdf"
                  onClick={() => {
                    if (document?.url) {
                      dispatch(showOverlay(true));
                      saveAs(document.url, document?.name);
                      dispatch(showOverlay());
                    }
                  }}
                  disabled={overlayDisplayed}
                >
                  <span className="material-icons-outlined">download</span>
                  <span>DOWNLOAD PDF</span>
                </Button>
                {!!document?.wordUrl && (
                  <Button
                    className="download-word secondary"
                    onClick={() => {
                      if (document?.wordUrl) {
                        dispatch(showOverlay(true));
                        saveAs(document.wordUrl, document?.wordName);
                        dispatch(showOverlay());
                      }
                    }}
                    disabled={overlayDisplayed}
                  >
                    <FontAwesomeIcon icon={faFileWord} size="lg" />
                    <span>DOWNLOAD DOC</span>
                  </Button>
                )}
              </div>
            </div>
          </object>
        </>
      )}
    </div>
  );
};

export default DocViewer;
